import React, { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../Layout/Layout';
import LocationsManager from '../Locations/LocationsManager';
import DateRangeMode from '../../components/schedule/DateRangeMode';
import CopyScheduleDialog from '../../components/schedule/CopyScheduleDialog';
import ScheduleNowNextBanner from '../../components/schedule/ScheduleNowNextBanner';
import { findOverlaps } from '../../components/schedule/overlaps';
import { useGetAllCompanyDevices } from '../../queries/devices';
import { useGetGroupsList } from '../../queries/group';
import {
  useGetCampaignSchedules, useCreateCampaignSchedule,
  useUpdateCampaignSchedule, useDeleteCampaignSchedule, useGetThemeOptions,
  useCopyCampaignSchedule, useSkipCampaignSchedule,
} from '../../queries/campaignSchedules';
import { useCafeScheduleStatus } from '../../queries/scheduleStatus';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useNavT } from '../../nav/useNavT';
import { useInHub } from '../../nav/useInHub';
import SubTabs from '../../nav/SubTabs';

/**
 * Cafe Theme Timeline (Cafe Scheduling redesign, Option A — feed model).
 *
 * Pick a device (or a whole group), then drag time blocks onto a 7-day × 24-hour
 * grid to schedule which THEME (campaign: background + music + corners) plays when.
 * Each block is one campaign_schedule (start_time / end_time / days_of_week mask /
 * priority, or a dated one-off). Times are VENUE WALL-CLOCK — sent verbatim, NO UTC
 * shift (the legacy browser-timezone bug). The backend resolves the active block in
 * the COMPANY timezone; the device feed + player alarms switch themes at the window
 * boundary. When nothing is scheduled, the device plays its default (EVENT) theme.
 *
 *   - Drag on an empty area of a day column → create a block (editor opens).
 *   - Click a block → edit theme / times / days / priority, or switch to a one-off
 *     date (holiday override = one-off + higher priority), or delete.
 */

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']; // bit 0..6
const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const HOUR_PX = 30;
const DAY_MIN = 24 * 60;
const SNAP_MIN = 15;
const TOTAL_PX = 24 * HOUR_PX;

const PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899',
  '#14B8A6', '#F97316', '#6366F1', '#84CC16', '#06B6D4',
  '#D946EF', '#EF4444', '#22C55E', '#EAB308', '#0EA5E9',
];
const colorFor = (id) => {
  let h = 0;
  for (let i = 0; i < String(id).length; i += 1) h = (h * 31 + String(id).charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const timeToMin = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(':');
  return (parseInt(h, 10) * 60) + parseInt(m, 10);
};
const minToInput = (min) => {
  const h = Math.floor(min / 60); const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const snap = (min) => Math.max(0, Math.min(DAY_MIN, Math.round(min / SNAP_MIN) * SNAP_MIN));

// 'YYYY-MM-DD' → weekday bit index 0..6 (Mon=0). Local date, no tz shift.
const dateToDayIndex = (d) => {
  if (!d) return null;
  const [y, mo, da] = String(d).slice(0, 10).split('-').map((x) => parseInt(x, 10));
  const js = new Date(y, mo - 1, da).getDay(); // 0=Sun..6=Sat
  return (js + 6) % 7; // → Mon=0..Sun=6
};
const fmtDateLocal = (dateObj) => {
  if (!dateObj) return null;
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Days a schedule applies to. One-off (start_date) → just that date's weekday;
// recurring → its mask (null/0 = every day).
const daysOf = (s) => {
  if (s.start_date) {
    const di = dateToDayIndex(s.start_date);
    return di === null ? [] : [di];
  }
  const mask = s.days_of_week;
  return (!mask ? [0, 1, 2, 3, 4, 5, 6] : DAY_KEYS.map((_, i) => i).filter((i) => mask & (1 << i)));
};

// Visual segment(s) for a schedule on a given day; a window past midnight splits.
const segmentsFor = (s) => {
  const st = timeToMin(s.start_time);
  const e = timeToMin(s.end_time);
  if (st === null || e === null) return [[0, DAY_MIN]];
  if (e > st) return [[st, e]];
  return [[st, DAY_MIN], [0, e]];
};

const layoutDay = (segs) => {
  const sorted = [...segs].sort((x, y) => x.start - y.start || x.end - y.end);
  const out = [];
  let cluster = []; let clusterEnd = -1;
  const flush = () => {
    if (!cluster.length) return;
    const lanes = [];
    cluster.forEach((seg) => {
      let placed = false;
      for (let i = 0; i < lanes.length; i += 1) {
        if (seg.start >= lanes[i]) { seg.lane = i; lanes[i] = seg.end; placed = true; break; }
      }
      if (!placed) { seg.lane = lanes.length; lanes.push(seg.end); }
    });
    cluster.forEach((seg) => { seg.laneCount = lanes.length; out.push(seg); });
    cluster = []; clusterEnd = -1;
  };
  sorted.forEach((seg) => {
    if (cluster.length && seg.start >= clusterEnd) flush();
    cluster.push(seg); clusterEnd = Math.max(clusterEnd, seg.end);
  });
  flush();
  return out;
};

// Laravel JsonResource wrapping is ON, so list endpoints may return { data: [...] }
// rather than a bare array — coerce defensively so .map() never blows up the page.
const asArray = (v) => (Array.isArray(v) ? v : (Array.isArray(v?.data) ? v.data : []));

export default function CafeThemeSchedule() {
  const nt = useNavT(); // labels only: returns the original English literal unless the new navigation is on
  const inHub = useInHub();
  const { data: devicesRaw } = useGetAllCompanyDevices();
  const { data: groupsRaw } = useGetGroupsList(null);
  const { data: themesRaw } = useGetThemeOptions();
  const devices = asArray(devicesRaw);
  const groups = asArray(groupsRaw);
  const themes = asArray(themesRaw);

  // Preselect a target when arrived via a device/group "Assign Schedule"
  // shortcut (?device=… or ?group=…) — same pattern as the video-wall schedule page.
  const [searchParams] = useSearchParams();
  const initialDevice = searchParams.get('device');
  const initialGroup = searchParams.get('group');

  const [view, setView] = useState('schedule'); // 'schedule' | 'locations'
  const [targetType, setTargetType] = useState(initialGroup ? 'group' : 'device'); // 'device' | 'group'
  const [deviceId, setDeviceId] = useState(initialDevice || null);
  const [groupId, setGroupId] = useState(initialGroup || null);
  const targetId = targetType === 'group' ? groupId : deviceId;

  const queryParams = targetType === 'group'
    ? (groupId ? { group_id: groupId } : null)
    : (deviceId ? { device_id: deviceId } : null);
  const { data: schedules = [] } = useGetCampaignSchedules(queryParams || {});

  const create = useCreateCampaignSchedule();
  const update = useUpdateCampaignSchedule();
  const del = useDeleteCampaignSchedule();
  const skip = useSkipCampaignSchedule();
  const copy = useCopyCampaignSchedule();

  const status = useCafeScheduleStatus({ type: targetType, id: targetId });
  // The status strip and bulk-copy are scheduling_pro features: hidden (never asked) when the package explicitly lacks it.
  const { has } = useEntitlements();
  const proOn = has('scheduling_pro');

  const gridRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [editor, setEditor] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);

  // Conflict detection: device rows are more specific (0) than group rows (1).
  const overlaps = useMemo(
    () => findOverlaps(schedules, (r) => (r.schedulable_type === 'device' ? 0 : 1)),
    [schedules],
  );

  const deviceOptions = devices.map((d) => ({ label: d.name || d.serial_name, value: d.id }));
  const groupOptions = (groups || []).map((g) => ({ label: g.name, value: g.id }));
  const themeOptions = themes.map((c) => ({ label: c.name, value: c.id }));
  const themeName = useMemo(() => {
    const m = {}; themes.forEach((c) => { m[c.id] = c.name; }); return m;
  }, [themes]);

  // Timezone note for the selected target — shows the device's actual branch + tz
  // (Device::resolvedTimezone), so the owner knows exactly which clock drives it.
  const tzNote = useMemo(() => {
    if (targetType === 'device' && deviceId) {
      const d = devices.find((x) => x.id === deviceId);
      if (d?.timezone) {
        return d.location_name
          ? nt('nav6.sched.tz_branch', "Times are in {{branch}}'s local time ({{tz}}).", { branch: d.location_name, tz: d.timezone })
          : nt('nav6.sched.tz_screen', "Times are in this screen's local time ({{tz}}).", { tz: d.timezone });
      }
    }
    if (targetType === 'group') return nt('nav6.sched.tz_group', "Times are in each screen's branch local time.");
    return nt('nav6.sched.tz_venue', "Times are in your venue's local time.");
  }, [targetType, deviceId, devices, nt]);

  const dayBlocks = useMemo(() => {
    const perDay = DAY_KEYS.map(() => []);
    schedules.forEach((s) => {
      const segs = segmentsFor(s);
      daysOf(s).forEach((di) => {
        segs.forEach(([start, end]) => {
          if (end <= start) return;
          perDay[di].push({ s, start, end, lane: 0, laneCount: 1 });
        });
      });
    });
    return perDay.map((segs) => layoutDay(segs));
  }, [schedules]);

  // ── drag-to-create ──────────────────────────────────────────────────────
  const yToMin = (clientY) => {
    const rect = gridRef.current.getBoundingClientRect();
    return snap(((clientY - rect.top) / rect.height) * DAY_MIN);
  };
  const onColPointerDown = (di, e) => {
    if (!targetId) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const m = yToMin(e.clientY);
    setDrag({ dayIndex: di, y0: m, y1: m });
  };
  const onColPointerMove = (e) => {
    if (!drag) return;
    setDrag((d) => ({ ...d, y1: yToMin(e.clientY) }));
  };
  const parseDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`) : null);

  const onColPointerUp = () => {
    if (!drag) return;
    const start = Math.min(drag.y0, drag.y1);
    let end = Math.max(drag.y0, drag.y1);
    if (end - start < SNAP_MIN) end = Math.min(DAY_MIN, start + SNAP_MIN);
    setEditor({
      id: null, campaignId: themeOptions[0]?.value ?? null,
      start: minToInput(start), end: minToInput(end), priority: 0,
      range: { mode: 'weekly', daysMask: 1 << drag.dayIndex, startDate: null, endDate: null },
    });
    setDrag(null);
  };

  const openBlock = (s) => setEditor({
    id: s.id, campaignId: s.campaign_id,
    start: s.start_time ? String(s.start_time).slice(0, 5) : '08:00',
    end: s.end_time ? String(s.end_time).slice(0, 5) : '12:00',
    priority: s.priority || 0,
    range: s.start_date
      ? { mode: 'range', daysMask: s.days_of_week || 0, startDate: parseDate(s.start_date), endDate: parseDate(s.end_date || s.start_date) }
      : { mode: 'weekly', daysMask: s.days_of_week || 0, startDate: null, endDate: null },
    nextOccurrenceDate: s.next_occurrence_date || null,
    nextOccurrenceSkipped: !!s.next_occurrence_skipped,
  });

  // Map the editor's recurrence (weekly | date-range) → API date/day fields.
  const rangeToFields = (r) => {
    if (r.mode === 'range') {
      return {
        days_of_week: r.daysMask || null,                 // null = every day in range
        start_date: fmtDateLocal(r.startDate),
        end_date: fmtDateLocal(r.endDate || r.startDate),
      };
    }
    return { days_of_week: r.daysMask, start_date: null, end_date: null };
  };

  const saveEditor = async () => {
    const ed = editor;
    if (!ed.campaignId) return;
    const r = ed.range;
    if (r.mode === 'weekly' && !r.daysMask) return; // need ≥1 weekday
    if (r.mode === 'range' && !r.startDate) return; // need a start date

    const schedule = {
      start_time: `${ed.start}:00`,
      end_time: `${ed.end}:00`,
      ...rangeToFields(r),
      priority: ed.priority,
    };
    if (ed.id) {
      await update.mutateAsync({ id: ed.id, campaign_id: ed.campaignId, ...schedule });
    } else {
      await create.mutateAsync({
        campaign_id: ed.campaignId,
        schedulable_type: targetType,
        schedulable_id: targetId,
        ...schedule,
      });
    }
    setEditor(null);
  };

  const removeEditor = () => {
    const id = editor.id;
    setEditor(null);
    confirmDialog({
      message: nt('nav6.sched.confirm_remove', 'Remove this scheduled theme?'),
      header: nt('nav6.sched.confirm_remove_title', 'Delete schedule'),
      icon: 'pi pi-exclamation-triangle', acceptClassName: 'p-button-danger',
      acceptLabel: nt('nav6.sched.delete', 'Delete'), rejectLabel: nt('nav6.sched.cancel', 'Cancel'),
      accept: () => del.mutate(id),
    });
  };

  // Toggles the next scheduled run: sits it out (recurring block itself stays and
  // fires again next time, e.g. "Carrefour wants something different this week
  // for their weekly promo slot"), or — if already sat out — restores it.
  const skipEditorOccurrence = () => {
    const id = editor.id;
    const wasSkipped = editor.nextOccurrenceSkipped;
    const dateLabel = editor.nextOccurrenceDate ? ` (${editor.nextOccurrenceDate})` : '';
    setEditor(null);
    confirmDialog({
      message: wasSkipped
        ? nt('nav6.sched.restore_msg', 'Restore this occurrence{{date}}? It will play again as scheduled.', { date: dateLabel })
        : nt('nav6.sched.skip_msg', 'Skip the next run of this theme{{date}}? The recurring schedule stays intact and resumes at its next scheduled time.', { date: dateLabel }),
      header: wasSkipped ? nt('nav6.sched.undo_skip_title', 'Undo skip') : nt('nav6.sched.skip_title', 'Skip this occurrence'),
      icon: wasSkipped ? 'pi pi-replay' : 'pi pi-forward',
      acceptLabel: wasSkipped ? nt('nav6.sched.undo', 'Undo') : nt('nav6.sched.skip', 'Skip'), rejectLabel: nt('nav6.sched.cancel', 'Cancel'),
      accept: () => skip.mutate({ id }),
    });
  };

  // Copy-to-other-targets candidates (exclude the current target).
  const copyTargets = useMemo(() => {
    const devs = deviceOptions
      .filter((o) => !(targetType === 'device' && o.value === deviceId))
      .map((o) => ({ label: `📺 ${o.label}`, value: o.value, type: 'device' }));
    const grps = groupOptions
      .filter((o) => !(targetType === 'group' && o.value === groupId))
      .map((o) => ({ label: `👥 ${o.label}`, value: o.value, type: 'group' }));
    return [...devs, ...grps];
  }, [deviceOptions, groupOptions, targetType, deviceId, groupId]);

  const applyCopy = (dests) => copy.mutateAsync({
    from: { type: targetType, id: targetId },
    to: dests,
    mode: 'replace',
  });

  return (
    <Layout>
      <ConfirmDialog />
      {!inHub && (
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
            <i className="pi pi-calendar me-2" style={{ color: 'var(--pp-primary)' }} />
            {nt('nav6.sched.title', 'Theme Schedule')}
          </h2>
          <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>
            {nt('nav6.sched.subtitle', 'Schedule which theme (background + music + corners) plays through the day.')}
          </p>
        </div>
      </div>
      )}

      {/* Tabs: Theme Schedule | Locations */}
      {inHub ? (
        // Branches have ONE home (الحساب › الفروع), so this row has no Locations tab; a single tab draws no row.
        <SubTabs ariaLabel={nt('nav6.sched.title', 'Theme Schedule')} items={[
          { id: 'schedule', label: nt('nav6.sched.title', 'Theme Schedule'), active: view === 'schedule', onClick: () => setView('schedule') },
        ]} />
      ) : (
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button label={nt('nav6.sched.title', 'Theme Schedule')} icon="pi pi-calendar" size="small" outlined={view !== 'schedule'} onClick={() => setView('schedule')} />
        <Button label={nt('nav6.sched.locations', 'Locations')} icon="pi pi-map-marker" size="small" outlined={view !== 'locations'} onClick={() => setView('locations')} />
      </div>
      )}

      {view === 'locations' && <LocationsManager />}

      {view === 'schedule' && (<>
      {/* Target picker */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button label={nt('nav6.sched.device', 'Device')} size="small" outlined={targetType !== 'device'} onClick={() => setTargetType('device')} />
        <Button label={nt('nav6.sched.group', 'Group')} size="small" outlined={targetType !== 'group'} onClick={() => setTargetType('group')} />
        {targetType === 'device' ? (
          <Dropdown value={deviceId} options={deviceOptions} onChange={(e) => setDeviceId(e.value)}
            placeholder={nt('nav6.sched.pick_device', 'Pick a device')} style={{ minWidth: 260 }} filter />
        ) : (
          <Dropdown value={groupId} options={groupOptions} onChange={(e) => setGroupId(e.value)}
            placeholder={nt('nav6.sched.pick_group', 'Pick a group')} style={{ minWidth: 260 }} filter emptyMessage={nt('nav6.sched.no_groups', 'No groups')} />
        )}
        <small style={{ color: 'var(--pp-text-secondary)' }}>
          <i className="pi pi-clock me-1" />{tzNote}
        </small>
      </div>

      {!targetId ? (
        <div className="text-center p-5" style={{ background: 'var(--pp-bg-secondary)', border: '1px dashed var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
          <div style={{ fontSize: '3rem' }}>🗓️</div>
          <h4 className="mt-3" style={{ color: 'var(--pp-text-primary)' }}>{nt('nav6.sched.pick_device_or_group', 'Pick a device or group')}</h4>
          <p style={{ color: 'var(--pp-text-secondary)' }}>{nt('nav6.sched.choose_where', 'Choose where to schedule themes, then drag on the grid to add a block.')}</p>
          {nt.on && has('locations') && (
            <p className="small mb-0"><Link to="/schedule/locations">{nt('nav6.sched.manage_branches', 'Manage branches')}</Link></p>
          )}
        </div>
      ) : (
        <>
          {/* Now playing / next transition (shared engine — matches what actually plays) */}
          {proOn && <ScheduleNowNextBanner status={status.data} loading={status.isLoading} uniform={status.data?.uniform ?? true}
            {...(nt.on ? { noneLabel: nt('nav6.sched.now_none', 'Default theme is playing (nothing scheduled now).'), nowLabel: (name) => nt('nav6.sched.now', 'Now: {{name}}', { name }), nextLabel: (name, at, day) => nt('nav6.sched.next', 'Next: {{name}} at {{at}} ({{day}})', { name, at, day }), nextNone: nt('nav6.sched.next_none', 'No upcoming change in the next 7 days.'), notUniform: nt('nav6.sched.not_uniform', 'Screens in this group are in different timezones — times shown are approximate.'), loadingLabel: nt('nav6.sched.loading', 'Loading…') } : {})} />}

          {/* Toolbar: copy-to-other-targets + overlap warning */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            {overlaps.count > 0 ? (
              <small style={{ color: 'var(--pp-warning)' }}>
                <i className="pi pi-exclamation-triangle me-1" />
                {nt.on
                  ? nt('nav6.sched.overlap', '{{count}} overlapping blocks — the higher-priority / narrower one plays.', { count: overlaps.count })
                  : <>{overlaps.count} overlapping block{overlaps.count > 1 ? 's' : ''} — the higher-priority / narrower one plays.</>}
              </small>
            ) : <span />}
            {proOn && <Button label={nt('nav6.sched.copy_btn', 'Copy to other devices…')} icon="pi pi-copy" size="small" outlined
              disabled={schedules.length === 0} onClick={() => setCopyOpen(true)} />}
          </div>

          {themeOptions.length === 0 && (
            <div className="p-3 mb-3" style={{ background: 'var(--pp-bg-secondary)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-md)' }}>
              <small style={{ color: 'var(--pp-text-secondary)' }}>
                <i className="pi pi-info-circle me-1" />{nt('nav6.sched.no_themes', 'No themes yet — create a campaign first, then schedule it here.')}
              </small>
            </div>
          )}
          <small className="d-block mb-2" style={{ color: 'var(--pp-text-secondary)' }}>
            <i className="pi pi-info-circle me-1" />{nt('nav6.sched.drag_hint', 'Drag on a day to add a theme block.')}
            {' '}<strong>{nt('nav6.sched.auto_apply', 'Changes apply automatically — no need to restart the screen.')}</strong>
          </small>

          {/* Weekly grid */}
          <div style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)', overflow: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '48px repeat(7, minmax(90px, 1fr))', minWidth: 720 }}>
              <div style={{ borderBottom: '1px solid var(--pp-border)' }} />
              {DAY_KEYS.map((d) => (
                <div key={d} className="text-center py-2" style={{ borderBottom: '1px solid var(--pp-border)', borderInlineStart: '1px solid var(--pp-border)', color: 'var(--pp-text-primary)', fontWeight: 600, fontSize: 13 }}>
                  {nt('nav6.sched.day.' + d, DAY_LABELS[d])}
                </div>
              ))}

              {/* Hour gutter */}
              <div style={{ position: 'relative', height: TOTAL_PX }}>
                {[...Array(24)].map((_, h) => (
                  <div key={h} style={{ position: 'absolute', top: h * HOUR_PX - 7, right: 4, fontSize: 10, color: 'var(--pp-text-secondary)' }}>
                    {String(h).padStart(2, '0')}:00
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {DAY_KEYS.map((d, di) => (
                <div
                  key={d}
                  ref={di === 0 ? gridRef : undefined}
                  onPointerDown={(e) => onColPointerDown(di, e)}
                  onPointerMove={onColPointerMove}
                  onPointerUp={onColPointerUp}
                  style={{ position: 'relative', height: TOTAL_PX, borderInlineStart: '1px solid var(--pp-border)', cursor: targetId ? 'crosshair' : 'default', touchAction: 'none' }}
                >
                  {[...Array(24)].map((_, h) => (
                    <div key={h} style={{ position: 'absolute', top: h * HOUR_PX, left: 0, right: 0, borderTop: '1px solid var(--pp-border)', opacity: h % 6 === 0 ? 0.9 : 0.35 }} />
                  ))}

                  {dayBlocks[di].map((seg, idx) => {
                    const top = (seg.start / DAY_MIN) * TOTAL_PX;
                    const height = Math.max(14, ((seg.end - seg.start) / DAY_MIN) * TOTAL_PX);
                    const widthPct = 100 / seg.laneCount;
                    const c = colorFor(seg.s.campaign_id);
                    const isOneOff = !!seg.s.start_date;
                    const isShadowed = overlaps.shadowed.has(seg.s.id);
                    // next_occurrence_skipped is ONE date for the whole row — for a
                    // Mon/Wed/Fri block only the specific upcoming date is sat out, so
                    // only that date's weekday cell shows the indicator, not all three.
                    const isSkipped = !!seg.s.next_occurrence_skipped && dateToDayIndex(seg.s.next_occurrence_date) === di;
                    return (
                      <div
                        key={`${seg.s.id}-${idx}`}
                        onPointerDown={(e) => { e.stopPropagation(); }}
                        onClick={(e) => { e.stopPropagation(); openBlock(seg.s); }}
                        title={`${seg.s.campaign?.name || themeName[seg.s.campaign_id] || ''}${isSkipped ? nt('nav6.sched.blk_skipped', ' — skipped this occurrence') : ''}${isShadowed ? nt('nav6.sched.blk_overridden', ' — overridden where it overlaps a higher-priority block') : ''}`}
                        style={{
                          position: 'absolute', top, height,
                          left: `calc(${seg.lane * widthPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`,
                          background: `${c}26`, borderInlineStart: `3px solid ${c}`,
                          border: isShadowed ? '1px dashed var(--pp-warning)' : `1px solid ${c}80`,
                          opacity: isShadowed ? 0.6 : (isSkipped ? 0.5 : 1),
                          filter: isSkipped ? 'grayscale(65%)' : 'none',
                          borderRadius: 6, padding: '2px 5px', overflow: 'hidden', cursor: 'pointer', fontSize: 11, color: 'var(--pp-text-primary)',
                        }}
                      >
                        <div className="text-truncate" style={{ fontWeight: 600, textDecoration: isSkipped ? 'line-through' : 'none' }}>
                          {isShadowed && <i className="pi pi-exclamation-triangle me-1" style={{ fontSize: 9, color: 'var(--pp-warning)' }} />}
                          {isSkipped && <i className="pi pi-forward me-1" style={{ fontSize: 9, color: 'var(--pp-text-secondary)' }} />}
                          {isOneOff && <i className="pi pi-calendar me-1" style={{ fontSize: 9 }} />}
                          {seg.s.campaign?.name || themeName[seg.s.campaign_id] || nt('nav6.sched.theme_fallback', 'Theme')}
                        </div>
                        <div className="text-truncate" style={{ color: 'var(--pp-text-secondary)', fontSize: 10 }}>
                          {`${String(seg.s.start_time).slice(0, 5)}–${String(seg.s.end_time).slice(0, 5)}`}
                          {seg.s.priority ? ` · P${seg.s.priority}` : ''}
                        </div>
                        {isSkipped && (
                          <div style={{
                            position: 'absolute', top: 2, right: 2, lineHeight: '12px',
                            fontSize: 8, padding: '0 3px', borderRadius: 3,
                            background: 'var(--pp-bg-elevated)', color: 'var(--pp-text-secondary)', border: '1px solid var(--pp-border)',
                          }}>
                            {nt('nav6.sched.badge_skipped', 'Skipped')}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {drag && drag.dayIndex === di && (() => {
                    const a = Math.min(drag.y0, drag.y1); const b = Math.max(drag.y0, drag.y1);
                    return (
                      <div style={{
                        position: 'absolute', left: 2, right: 2,
                        top: (a / DAY_MIN) * TOTAL_PX, height: Math.max(2, ((b - a) / DAY_MIN) * TOTAL_PX),
                        background: 'var(--pp-btn-honey)', opacity: 0.25, border: '1px dashed var(--pp-btn-honey)', borderRadius: 6, pointerEvents: 'none',
                      }} />
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      </>)}

      {/* Block editor */}
      <Dialog
        visible={!!editor}
        onHide={() => setEditor(null)}
        header={editor?.id ? nt('nav6.sched.edit_block', 'Edit theme block') : nt('nav6.sched.new_block', 'Schedule a theme')}
        style={{ width: 'min(95vw, 480px)' }}
      >
        {editor && (
          <div className="d-flex flex-column gap-3 pt-2">
            <div>
              <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{nt('nav6.sched.theme', 'Theme')}</label>
              <Dropdown value={editor.campaignId} options={themeOptions} onChange={(e) => setEditor((ed) => ({ ...ed, campaignId: e.value }))}
                placeholder={nt('nav6.sched.pick_theme', 'Pick a theme')} className="w-100" filter />
            </div>

            <div className="d-flex align-items-center gap-2">
              <small style={{ color: 'var(--pp-text-secondary)', width: 44 }}>{nt('nav6.sched.from', 'From')}</small>
              <input type="time" step="900" value={editor.start} onChange={(e) => setEditor((ed) => ({ ...ed, start: e.target.value }))} />
              <small style={{ color: 'var(--pp-text-secondary)', width: 24 }}>{nt('nav6.sched.to', 'To')}</small>
              <input type="time" step="900" value={editor.end} onChange={(e) => setEditor((ed) => ({ ...ed, end: e.target.value }))} />
            </div>

            <DateRangeMode
              value={editor.range}
              onChange={(range) => setEditor((ed) => ({ ...ed, range }))}
              dayLabels={DAY_KEYS.map((d) => nt('nav6.sched.day.' + d, DAY_LABELS[d]))}
              labels={nt.on ? { weekly: nt('nav6.sched.drm.weekly', 'Repeat weekly'), range: nt('nav6.sched.drm.range', 'Date range'), from: nt('nav6.sched.drm.from', 'From'), to: nt('nav6.sched.drm.to', 'To'), daysHint: nt('nav6.sched.drm.days_hint', 'Pick the weekdays it repeats on.'), rangeHint: nt('nav6.sched.drm.range_hint', 'Plays only within this date range. Leave weekdays empty for every day; give it a higher priority to override the weekly plan.'), rangeDaysHint: nt('nav6.sched.drm.range_days_hint', 'Optional — limit to certain weekdays inside the range (empty = every day).') } : undefined}
            />

            <div className="d-flex align-items-center gap-2">
              <small style={{ color: 'var(--pp-text-secondary)' }}>{nt('nav6.sched.priority', 'Priority')}</small>
              <InputNumber value={editor.priority} min={0} max={999} onValueChange={(e) => setEditor((ed) => ({ ...ed, priority: e.value || 0 }))} inputStyle={{ width: 80 }} />
              <small style={{ color: 'var(--pp-text-secondary)' }}>{nt('nav6.sched.priority_hint', 'Higher wins when blocks overlap.')}</small>
            </div>

            <div className="d-flex justify-content-between align-items-center gap-2 pt-2" style={{ borderTop: '1px solid var(--pp-border)' }}>
              <div className="d-flex gap-2">
                {editor.id ? (
                  <Button label={nt('nav6.sched.delete', 'Delete')} icon="pi pi-trash" text severity="danger" onClick={removeEditor} />
                ) : null}
                {editor.id && editor.range?.daysMask ? (
                  <Button
                    label={editor.nextOccurrenceSkipped ? nt('nav6.sched.undo_skip_btn', 'Undo Skip') : nt('nav6.sched.skip_title', 'Skip this occurrence')}
                    icon={editor.nextOccurrenceSkipped ? 'pi pi-replay' : 'pi pi-forward'}
                    text loading={skip.isLoading} onClick={skipEditorOccurrence}
                  />
                ) : null}
              </div>
              <div className="d-flex gap-2">
                <Button label={nt('nav6.sched.cancel', 'Cancel')} text onClick={() => setEditor(null)} />
                <Button label={nt('nav6.sched.save', 'Save')} icon="pi pi-check"
                  loading={create.isLoading || update.isLoading}
                  disabled={!editor.campaignId
                    || (editor.range?.mode === 'weekly' && !editor.range?.daysMask)
                    || (editor.range?.mode === 'range' && !editor.range?.startDate)}
                  onClick={saveEditor} />
              </div>
            </div>
          </div>
        )}
      </Dialog>

      <CopyScheduleDialog
        visible={copyOpen}
        onHide={() => setCopyOpen(false)}
        title={nt('nav6.sched.copy_title', 'Copy this schedule to other devices')}
        sourceLabel={nt.on
          ? nt('nav6.sched.copy_source', 'Copying {{count}} block(s) from the selected {{target}}.', { count: schedules.length, target: targetType === 'group' ? nt('nav6.sched.group_lc', 'group') : nt('nav6.sched.device_lc', 'device') })
          : `Copying ${schedules.length} block${schedules.length === 1 ? '' : 's'} from the selected ${targetType}.`}
        replaceWarning={nt('nav6.sched.copy_warning', 'This replaces the entire existing theme schedule on each selected target.')}
        targets={copyTargets}
        onApply={applyCopy}
        loading={copy.isLoading}
      />
    </Layout>
  );
}
